import React from "react";
import { useTable, useSortBy, useFilters, Column } from "react-table";
import { useExportData } from "react-table-plugins";
import { useNavigate } from "react-router-dom";
import { ButtonWithoutIcon, CustomPagination } from "../../lib";
import { KTIcon } from "../../../../../_metronic/helpers";
import moment from "moment-jalaali";

interface User {
  id: number;
  national_code: string;
  mobile: string;
  created_at: string;
  status: boolean;
  actions?: string;
  f_name: string;
  l_name: string;
  male: boolean;
}

interface TableProps {
  data: {
    users: User[];
    totalRows: number;
  };
  queryString: {
    page: number | string;
    search: string;
    numberInPage: number | string;
  };
  setQueryString: (page: number) => void;
  refetch: () => void;
  numberInPage: number;
}

const FanReportTableUsers = ({
  data,
  queryString,
  setQueryString,
  refetch,
  numberInPage,
}: TableProps) => {
  const navigate = useNavigate();

  const columns = React.useMemo<Column<User>[]>(
    () => [
      {
        Header: <HeaderTextIcon text={"ردیف"} />,
        accessor: "id",
        Cell: ({ row }) => (
          <span className="rowNum">
            {(Number(queryString.page) - 1) * Number(numberInPage) +
              row.index +
              1}
          </span>
        ),
      },
      {
        Header: <HeaderTextIcon text={"نام و نام خانوادگی"} />,
        accessor: (row) => {
          const fullName = `${row.f_name || ""} ${row.l_name || ""}`.trim();
          return fullName || "نام وجود ندارد";
        },
        id: "fullName",
      },
      {
        Header: <HeaderTextIcon text={"کد ملی"} />,
        accessor: "national_code",
      },
      {
        Header: <HeaderTextIcon text={"جنسیت"} />,
        accessor: "male",
        Cell: ({ value }) => <span>{value ? "مرد" : "زن"}</span>,
      },
      {
        Header: <HeaderTextIcon text={"موبایل"} />,
        accessor: "mobile",
      },
      {
        Header: <HeaderTextIcon text={"تاریخ ثبت نام"} />,
        accessor: "created_at",
        Cell: ({ value }) => (
          <span>{moment(value).format("jYYYY/jMM/jDD HH:mm")}</span>
        ),
      },

      {
        Header: "",
        accessor: "actions",
        Cell: ({ row }) => (
          <div className={"d-flex gap-3 justify-content-center"}>
            <ButtonWithoutIcon
              btnText={"جزئیات"}
              type={"click"}
              onClick={() => {
                navigate("/reports/fan-users-log", {
                  state: { userId: row.original.id },
                });
              }}
            />
          </div>
        ),
      },
    ],
    [queryString.page, numberInPage, navigate]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: data.users || [],
      },
      useFilters,
      useSortBy,
      useExportData
    );

  return (
    <div className={"p-c-30"}>
      <div style={{ overflow: "auto" }}>
        <table
          {...getTableProps()}
          className="table align-middle table-row-dashed fs-6 gy-5"
        >
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()}>
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="d-flex justify-content-end mt-5">
        <CustomPagination
          activePage={Number(queryString.page)}
          setQueryString={setQueryString}
          totalRows={data?.totalRows}
          numberInPage={numberInPage}
        />
      </div>
    </div>
  );
};

const HeaderTextIcon = ({ text }: { text: string }) => (
  <div className={"d-flex align-items-center justify-content-center gap-2"}>
    <span>{text}</span>
    <KTIcon iconName={"filter"} className={"fs-5"} />
  </div>
);

export default FanReportTableUsers;
